import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, X, Search, Loader2, Check } from "lucide-react";
import { crud } from "../lib/api";
import { FK_RESOURCES } from "../lib/choices";

export default function SearchableSelect(props) {
    if (props.multi) return <MultiSearchableSelect {...props} />;
    return <SingleSearchableSelect {...props} />;
}

function displayLabel(cfg, obj) {
    if (!obj) return "";
    if (obj.__label) return obj.__label;
    return cfg.labelFor(obj);
}

function sameId(a, b) {
    if (a == null || b == null) return false;
    return String(a) === String(b);
}

function multiValueIds(value) {
    return normalizeMultiValue(value)
        .map((item) => String(item.id))
        .sort()
        .join(",");
}

function normalizeMultiValue(value) {
    if (value == null || value === "") return [];
    if (
        typeof value === "object" &&
        !Array.isArray(value) &&
        Array.isArray(value.id)
    ) {
        return value.id
            .filter((id) => id != null && id !== "")
            .map((id) => ({ id, __idOnly: true }));
    }
    const arr = Array.isArray(value) ? value : [value];
    return arr
        .flatMap((v) => {
            if (v == null || v === "") return [];
            if (Array.isArray(v)) {
                return v
                    .filter((id) => id != null && id !== "")
                    .map((id) => ({ id, __idOnly: true }));
            }
            if (typeof v === "object" && v.id !== undefined) {
                if (Array.isArray(v.id)) {
                    return v.id
                        .filter((id) => id != null && id !== "")
                        .map((id) => ({ id, __idOnly: true }));
                }
                return [v];
            }
            return [{ id: v, __idOnly: true }];
        })
        .filter(Boolean);
}

function useDropdownPosition(open, anchorRef, dropdownDirection = "down") {
    const [style, setStyle] = useState(null);

    useLayoutEffect(() => {
        if (!open || !anchorRef.current) {
            setStyle(null);
            return;
        }
        function update() {
            const el = anchorRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const gap = 4;
            const maxH = 240;
            if (dropdownDirection === "up") {
                setStyle({
                    position: "fixed",
                    left: rect.left,
                    width: rect.width,
                    bottom: window.innerHeight - rect.top + gap,
                    maxHeight: Math.min(maxH, rect.top - gap - 8),
                    zIndex: 250,
                });
            } else {
                setStyle({
                    position: "fixed",
                    left: rect.left,
                    width: rect.width,
                    top: rect.bottom + gap,
                    maxHeight: Math.min(
                        maxH,
                        window.innerHeight - rect.bottom - gap - 8,
                    ),
                    zIndex: 250,
                });
            }
        }
        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
        };
    }, [open, anchorRef, dropdownDirection]);

    return style;
}

function DropdownResults({ loading, results, cfg, selected, onPick }) {
    if (loading) {
        return (
            <div className="px-3 py-4 text-xs text-slate-500 flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Aranıyor...
            </div>
        );
    }
    if (results.length === 0) {
        return (
            <div className="px-3 py-4 text-xs text-slate-500">
                Sonuç bulunamadı.
            </div>
        );
    }
    return results.map((o) => {
        const isSel = sameId(selected?.id, o.id);
        const sec = cfg.secondary(o);
        return (
            <button
                key={o.id}
                type="button"
                onClick={() => onPick(o)}
                className={`w-full text-left px-3 py-2 hover:bg-white/5 transition flex items-center gap-2 ${
                    isSel ? "bg-[#D4AF37]/8" : ""
                }`}
            >
                <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-100 truncate">
                        {displayLabel(cfg, o)}
                    </div>
                    {sec ? (
                        <div className="text-[11px] text-slate-500 truncate font-mono-jb">
                            {sec}
                        </div>
                    ) : null}
                </div>
                {isSel ? <Check className="h-4 w-4 text-[#F3E5AB]" /> : null}
            </button>
        );
    });
}

function SingleSearchableSelect({
    value,
    onChange,
    resource,
    placeholder = "Seçiniz...",
    required = false,
    testId,
    labelHint = "",
    dropdownDirection = "down",
    listParams,
}) {
    const cfg = FK_RESOURCES[resource] || {
        labelFor: (o) =>
            o?.__label || o?.name || o?.title || (o?.id != null ? `#${o.id}` : ""),
        secondary: () => "",
    };

    const initialId = useMemo(() => {
        if (value === null || value === undefined || value === "") return null;
        if (typeof value === "object") return value.id ?? null;
        if (typeof value === "number") return value;
        const s = String(value);
        if (/^\d+$/.test(s)) return Number(s);
        if (
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                s,
            )
        )
            return s;
        return null;
    }, [value]);

    const initialObj = useMemo(() => {
        if (value && typeof value === "object") return value;
        return null;
    }, [value]);

    const initialFallbackText = useMemo(() => {
        if (typeof value === "string" && !/^\d+$/.test(value)) return value;
        return "";
    }, [value]);

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const seedSelected = useMemo(() => {
        if (initialObj) return initialObj;
        if (!initialId) return null;
        if (labelHint) return { id: initialId, __label: labelHint };
        return { id: initialId, __idOnly: true };
    }, [initialId, initialObj, labelHint]);

    const [selected, setSelected] = useState(seedSelected);

    const wrapRef = useRef(null);
    const panelRef = useRef(null);
    const anchorRef = useRef(null);
    const searchRef = useRef(null);
    const debounceRef = useRef(null);
    const panelStyle = useDropdownPosition(open, anchorRef, dropdownDirection);

    useEffect(() => {
        setSelected(seedSelected);
    }, [seedSelected]);

    useEffect(() => {
        let cancel = false;
        if (initialObj || !initialId) return undefined;
        async function hydrate() {
            try {
                const obj = await crud.retrieve(resource, initialId);
                if (!cancel) setSelected(obj);
            } catch {
                if (!cancel && labelHint) {
                    setSelected({ id: initialId, __label: labelHint });
                }
            }
        }
        hydrate();
        return () => {
            cancel = true;
        };
    }, [initialId, resource, initialObj, labelHint]);

    useEffect(() => {
        function onDocClick(e) {
            const t = e.target;
            if (wrapRef.current?.contains(t)) return;
            if (panelRef.current?.contains(t)) return;
            setOpen(false);
        }
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    useEffect(() => {
        if (!open) return;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const params = { page_size: 50, ...(listParams || {}) };
                if (query) params.search = query;
                const data = await crud.list(resource, params);
                const items = Array.isArray(data) ? data : data.results || [];
                setResults(items);
            } catch {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 220);
        return () => clearTimeout(debounceRef.current);
    }, [query, open, resource, listParams]);

    function pick(o) {
        setSelected(o);
        onChange?.(o?.id ?? null, { label: displayLabel(cfg, o) });
        setOpen(false);
        setQuery("");
    }

    function clear(e) {
        e.stopPropagation();
        setSelected(null);
        onChange?.(null, { label: "" });
    }

    const labelText =
        (selected && displayLabel(cfg, selected)) ||
        labelHint ||
        initialFallbackText ||
        (initialId != null ? `#${initialId}` : "");

    const baseInput =
        "w-full bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition";

    const dropdownPanel =
        open &&
        panelStyle &&
        createPortal(
            <div
                ref={panelRef}
                style={panelStyle}
                className="rounded-md border border-white/10 bg-[#0F1626] shadow-2xl overflow-hidden flex flex-col"
            >
                <div className="p-2 border-b border-white/10 bg-[#0B0F19] shrink-0">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
                        <input
                            ref={searchRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Ara..."
                            className="w-full bg-[#121A2F] border border-white/10 rounded-md pl-8 pr-2 py-1.5 text-xs text-slate-100 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none"
                        />
                    </div>
                </div>
                <div className="overflow-y-auto flex-1 min-h-0">
                    <DropdownResults
                        loading={loading}
                        results={results}
                        cfg={cfg}
                        selected={selected}
                        onPick={pick}
                    />
                </div>
            </div>,
            document.body,
        );

    return (
        <div ref={wrapRef} className="relative">
            <button
                ref={anchorRef}
                type="button"
                data-testid={testId}
                onClick={() => {
                    setOpen((v) => !v);
                    setTimeout(() => searchRef.current?.focus(), 50);
                }}
                className={`${baseInput} text-left flex items-center gap-2 ${
                    open ? "border-[#D4AF37] ring-1 ring-[#D4AF37]" : ""
                }`}
            >
                <span className={labelText ? "flex-1 truncate" : "flex-1 text-slate-600"}>
                    {labelText || placeholder}
                </span>
                {selected && !required ? (
                    <span
                        role="button"
                        tabIndex={-1}
                        onClick={clear}
                        className="h-5 w-5 rounded hover:bg-white/10 flex items-center justify-center text-slate-500 hover:text-white"
                        title="Temizle"
                    >
                        <X className="h-3.5 w-3.5" />
                    </span>
                ) : null}
                <ChevronDown
                    className={`h-4 w-4 text-slate-500 transition-transform ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </button>
            {dropdownPanel}
        </div>
    );
}

function MultiSearchableSelect({
    value,
    onChange,
    resource,
    placeholder = "Seçiniz...",
    testId,
}) {
    const cfg = FK_RESOURCES[resource] || {
        labelFor: (o) => o?.name || o?.title || `#${o?.id}`,
        secondary: () => "",
    };
    const baseItems = useMemo(() => normalizeMultiValue(value), [value]);
    const idsKey = useMemo(() => multiValueIds(value), [value]);

    const [labelCache, setLabelCache] = useState(() => new Map());
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const wrapRef = useRef(null);
    const debounceRef = useRef(null);

    const items = useMemo(
        () =>
            baseItems.map((item) => {
                if (!item.__idOnly) return item;
                const cached = labelCache.get(String(item.id));
                return cached || item;
            }),
        [baseItems, labelCache],
    );

    useEffect(() => {
        let cancel = false;
        async function hydrate() {
            const idOnly = baseItems.filter((i) => i.__idOnly);
            if (idOnly.length === 0) return;
            const fetched = await Promise.all(
                idOnly.map((i) =>
                    crud
                        .retrieve(resource, i.id)
                        .catch(() => ({ id: i.id })),
                ),
            );
            if (cancel) return;
            setLabelCache((prev) => {
                const next = new Map(prev);
                for (const row of fetched) {
                    if (row?.id != null) next.set(String(row.id), row);
                }
                return next;
            });
        }
        hydrate();
        return () => {
            cancel = true;
        };
    }, [resource, idsKey, baseItems]);

    useEffect(() => {
        function onDocClick(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    useEffect(() => {
        if (!open) return;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const params = { page_size: 50 };
                if (query) params.search = query;
                const data = await crud.list(resource, params);
                const list = Array.isArray(data) ? data : data.results || [];
                setResults(list);
            } catch {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 220);
        return () => clearTimeout(debounceRef.current);
    }, [query, open, resource]);

    function currentIds() {
        return baseItems.map((i) => i.id);
    }

    function add(o) {
        const ids = currentIds();
        if (ids.some((id) => sameId(id, o.id))) return;
        onChange?.([...ids, o.id]);
        setLabelCache((prev) => {
            const next = new Map(prev);
            next.set(String(o.id), o);
            return next;
        });
        setQuery("");
    }

    function remove(id) {
        onChange?.(currentIds().filter((itemId) => !sameId(itemId, id)));
    }

    return (
        <div ref={wrapRef} className="relative">
            <div
                onClick={() => setOpen(true)}
                data-testid={testId}
                className="w-full bg-[#0B0F19] border border-white/10 rounded-md px-2 py-1.5 text-sm flex flex-wrap gap-1.5 items-center min-h-[40px] cursor-text focus-within:border-[#D4AF37] focus-within:ring-1 focus-within:ring-[#D4AF37]"
            >
                {items.length === 0 && (
                    <span className="text-slate-600 px-1">{placeholder}</span>
                )}
                {items.map((i) => (
                    <span
                        key={String(i.id)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#F3E5AB] text-xs"
                    >
                        <span className="truncate max-w-[160px]">
                            {displayLabel(cfg, i)}
                        </span>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                remove(i.id);
                            }}
                            className="hover:text-rose-300"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    </span>
                ))}
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setOpen(true)}
                    placeholder={items.length ? "Ara…" : ""}
                    className="flex-1 min-w-[80px] bg-transparent outline-none px-1 text-sm text-slate-100 placeholder:text-slate-600"
                />
                <ChevronDown
                    className={`h-4 w-4 text-slate-500 transition-transform ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </div>

            {open && (
                <div className="absolute z-30 mt-1 w-full rounded-md border border-white/10 bg-[#0F1626] shadow-2xl overflow-hidden">
                    <div className="max-h-60 overflow-y-auto">
                        <DropdownResults
                            loading={loading}
                            results={results}
                            cfg={cfg}
                            selected={null}
                            onPick={add}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
